import { Color, Icon } from '@billy/design-system'
import React, { forwardRef, HTMLAttributes, ReactElement, Ref } from 'react'

import { ReactComponent as LogoSimpleSvg } from '../../assets/images/logo-app-simple.svg'
import { useThemeContext } from '../../contexts/themeProvider'
import { LogoVariant } from '../../types/logoVariant'
import { getClassName } from '../../utils/getClassName'
import { getImgPath } from '../../utils/getImgPath'
import * as Styled from './styles'

interface ImageParams {
  src: string
  width: number
  height: number
  whiteVersionSrc?: string
}

const logoToImage: Record<LogoVariant, ImageParams> = {
  logo: {
    src: getImgPath('/logo/logo-app-@2x.png'),
    width: 128,
    height: 28,
  },
  logoInverted: {
    src: getImgPath('/logo/logo-app-inverted-@2x.png'),
    width: 128,
    height: 29,
  },
  logoSimple: {
    src: getImgPath('/logo/logo-app-simple-@2x.png'),
    width: 34,
    height: 20,
  },
  logoSimpleSvg: {
    src: getImgPath('/logo/logo-app-simple.svg'),
    width: 34,
    height: 20,
  },
  logoSimpleInverted: {
    src: getImgPath('/logo/logo-app-simple-inverted-@2x.png'),
    width: 34,
    height: 20,
  },
  salary: {
    src: getImgPath('/logo/logo-salary.png'),
    width: 262,
    height: 40,
  },
}

interface LogoProps extends HTMLAttributes<HTMLElement> {
  logo?: LogoVariant
  isWhite?: boolean
  color?: Color
}

export const Logo = forwardRef(
  ({ onClick, logo, isWhite = false, color, ...rest }: LogoProps, forwardedRef: Ref<HTMLElement>): ReactElement => {
    const { themeLayout } = useThemeContext()
    const defaultLogoVariant: LogoVariant = themeLayout === 'dark' ? 'logoSimpleInverted' : 'logoSimple'
    const selectedLogo = logo || defaultLogoVariant
    const { src, width, height } = logoToImage[selectedLogo]

    const renderLogo = () => {
      if (color && selectedLogo === 'logoSimpleSvg') {
        return <Icon icon={LogoSimpleSvg} color={color} size="sm" style={{ width, height }} />
      }

      return <img src={src} width={width} height={height} alt="Logo" title="Billy by Shine" />
    }

    return (
      <Styled.LogoWrapper
        ref={forwardedRef}
        clickable={!!onClick}
        onClick={onClick}
        className={getClassName('logo')}
        {...rest}
      >
        {renderLogo()}
      </Styled.LogoWrapper>
    )
  },
)
